import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { fromEvent, interval, merge, throttleTime } from 'rxjs';

import { ApplicationRoles } from '@bn/primitives';
import { AuthStorage, UserDetails } from '@bn/shared/core/api';

import { SwitchComponent } from '../switch/switch.component';

const INACTIVITY_LOGOUT_DURATION_MS = 10 * 60 * 1000;
const COUNTDOWN_TICK_MS = 1000;
const ACTIVITY_THROTTLE_MS = 1000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'touchstart'] as const;

@Component({
  selector: 'bn-web-ui-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  imports: [FaIconComponent, SwitchComponent, FormsModule, TooltipDirective],
})
export class TopbarComponent {
  readonly #destroyRef = inject(DestroyRef);

  readonly blurThumbnails = input(false, { transform: (value?: boolean) => value ?? false });
  readonly authData = input({} as AuthStorage, { transform: (value: AuthStorage | null) => (value ? value : ({} as AuthStorage)) });
  readonly userDetails = input({} as UserDetails, { transform: (value?: UserDetails | null) => (value ? value : ({} as UserDetails)) });
  readonly clickOnLogout = output<boolean>();
  readonly clickOnBlurThumbnails = output<boolean>();

  public readonly remainingSeconds = signal(INACTIVITY_LOGOUT_DURATION_MS / 1000);
  public readonly remainingTime = computed(() => {
    const totalSeconds = this.remainingSeconds();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = `${totalSeconds % 60}`.padStart(2, '0');

    return `${minutes}:${seconds}`;
  });

  public readonly isSubuser = computed(() => this.userDetails().Roles?.includes(ApplicationRoles.Subuser) ?? false);

  public readonly isAgentSession = computed(() => !!this.authData()?.agent);
  public readonly isPartnerSession = computed(() => !!this.authData()?.partner);
  public readonly isVisible = computed(() => this.isAgentSession() || this.isPartnerSession());

  #sessionDeadline = Date.now() + INACTIVITY_LOGOUT_DURATION_MS;
  #hasLoggedOut = false;
  #isTrackingInactivity = false;

  constructor() {
    // Inactivity tracking is started from here rather than ngOnInit: `authData` arrives through a
    // signal input, so ngOnInit can run before the session is known and would skip tracking for good.
    effect(() => {
      if (!this.authData()) return;

      this.extendSession();

      if (this.isVisible() && !this.#isTrackingInactivity) {
        this.#isTrackingInactivity = true;
        this.#trackInactivity();
      }
    });
  }

  public extendSession(): void {
    if (this.#hasLoggedOut) return;

    // Activity arriving after the deadline (e.g. waking from a suspended tab before the
    // delayed countdown tick fires) must not revive an already-expired session
    if (Date.now() >= this.#sessionDeadline) {
      this.remainingSeconds.set(0);
      this.logout();
      return;
    }

    this.#sessionDeadline = Date.now() + INACTIVITY_LOGOUT_DURATION_MS;
    this.remainingSeconds.set(INACTIVITY_LOGOUT_DURATION_MS / 1000);
  }

  public logout(): void {
    this.#hasLoggedOut = true;
    this.clickOnLogout.emit(true);
  }

  onToggleBlurThumbnails(value: boolean): void {
    this.clickOnBlurThumbnails.emit(value);
  }

  // Deadline-based instead of decrement-based so the countdown stays correct
  // when the browser throttles timers in background tabs.
  #trackInactivity(): void {
    interval(COUNTDOWN_TICK_MS)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.onCountdownTick());

    // Capture phase so non-bubbling events (e.g. scroll inside overflow containers) still count as activity
    merge(...ACTIVITY_EVENTS.map((eventName) => fromEvent(document, eventName, { capture: true, passive: true })))
      .pipe(throttleTime(ACTIVITY_THROTTLE_MS), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.extendSession());
  }

  private onCountdownTick(): void {
    if (this.#hasLoggedOut) return;

    const remaining = Math.max(0, Math.ceil((this.#sessionDeadline - Date.now()) / 1000));
    this.remainingSeconds.set(remaining);

    if (remaining === 0) {
      this.logout();
    }
  }
}
